import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/** Gap in px between the bottom of the header and the top of the cart box */
const HEADER_GAP = 4;

/**
 * A custom element that manages the cart box.
 * Opens as a non-modal box anchored 4px below the header so the header stays visible and usable.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {HTMLElement | null} */
  #scrim = null;

  /** @type {Element | null} */
  #previouslyFocused = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#removeOpenListeners();
    this.#scrim?.remove();
    this.#scrim = null;
  }

  #handleCartAdd = () => {
    if (this.refs.dialog?.open) {
      // The cart re-render can shift the header, so re-anchor the open box once the new markup is in place
      requestAnimationFrame(this.#updatePosition);
      return;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previouslyFocused = document.activeElement;

    // Lock scroll first so the header measurement accounts for the removed scrollbar
    document.documentElement.setAttribute('scroll-lock', '');
    this.#updatePosition();

    dialog.show();
    this.#getScrim().classList.add('cart-scrim--visible');

    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeyDown);
    window.addEventListener('resize', this.#updatePosition);

    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.#removeOpenListeners();
    this.#scrim?.classList.remove('cart-scrim--visible');

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });

    const focusWasInside = dialog.contains(document.activeElement);

    dialog.close();
    dialog.classList.remove('dialog-closing');
    document.documentElement.removeAttribute('scroll-lock');

    if (focusWasInside && this.#previouslyFocused instanceof HTMLElement) {
      this.#previouslyFocused.focus({ preventScroll: true });
    }
    this.#previouslyFocused = null;

    this.dispatchEvent(new DialogCloseEvent());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  toggle() {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.open();
    }
  }

  /**
   * Anchors the box to the live header edges: top sits HEADER_GAP below the header,
   * left/right align with the header so the box lines up with the navbar.
   * The offsets live on <html> rather than the dialog, because cart updates morph the dialog
   * and would wipe its inline styles, snapping the box to the top of the screen.
   */
  #updatePosition = () => {
    const header = document.querySelector('#header-component');
    const viewportWidth = document.documentElement.clientWidth;

    let top = HEADER_GAP;
    let left = 0;
    let right = 0;

    if (header) {
      const rect = header.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && getComputedStyle(header).opacity !== '0';

      if (isVisible) top = rect.bottom + HEADER_GAP;
      left = Math.max(0, rect.left);
      right = Math.max(0, viewportWidth - rect.right);
    }

    const { style } = document.documentElement;
    style.setProperty('--cart-box-top', `${top}px`);
    style.setProperty('--cart-box-left', `${left}px`);
    style.setProperty('--cart-box-right', `${right}px`);
  };

  /**
   * Closes the box on clicks outside of it. The trigger lives inside this component, so it is ignored here.
   * composedPath is used because cart re-renders can detach the clicked node before the event bubbles.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (event.composedPath().includes(this)) return;
    this.closeDialog();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  #removeOpenListeners() {
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('resize', this.#updatePosition);
  }

  /**
   * The page scrim lives on <body> so it can sit beneath the header's stacking context.
   * @returns {HTMLElement}
   */
  #getScrim() {
    if (!this.#scrim) {
      this.#scrim = document.createElement('div');
      this.#scrim.className = 'cart-scrim';
      this.#scrim.setAttribute('aria-hidden', 'true');
      document.body.append(this.#scrim);
    }

    return this.#scrim;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
